import React, { useState, useEffect } from 'react';
import { Routes, Route, Link } from 'react-router-dom';
import Navbar from './components/Navbar';
import DealPage from './components/DealPage'; 
import { 
  collection, getDocs, addDoc, deleteDoc, doc, updateDoc, 
  query, orderBy, limit, setDoc, where, getDoc, writeBatch, serverTimestamp 
} from 'firebase/firestore';
import { 
  signInWithPopup, GoogleAuthProvider, signOut, onAuthStateChanged 
} from 'firebase/auth'; 
import { db, auth } from './firebase'; 
import { 
  Search, Filter, X, Zap, Trash2, Mail, Send, 
  AlertCircle, Heart, FolderInput, LogOut, User, Edit, Archive
} from 'lucide-react';
import Footer from './components/Footer';
import EvNews from './components/EvNews';
import CostsAndSavings from './components/CostsAndSavings';
import SectionToggle from './components/SectionToggle';

import { STORE_MAP, STORE_LOGOS, STORE_VARIANTS, Deal, getHighCommissionLink } from './utils/constants';
import DealCard from './components/DealCard';
import FilterSidebar from './components/FilterSidebar';
import SavedDeals from './components/SavedDeals';
import DealSkeleton from './components/DealSkeleton'; 
import Terms from './components/Terms';

const CATEGORY_LIST = ['All', 'Accessories', 'Charging & Power', 'Money Math', 'Upgrades'];

export default function Main() {
  // --- STATE ---
  const [deals, setDeals] = useState<Deal[]>([]);
  const [heroDeals, setHeroDeals] = useState<Deal[]>([]);
  const [loading, setLoading] = useState(true);
  const [featuredVideo, setFeaturedVideo] = useState<{videoId: string, title: string} | null>(null);

  // USER & AUTH STATE
  const [user, setUser] = useState<any>(null); 
  const [userData, setUserData] = useState<any>(null); 

  // VIEW STATE
  const [view, setView] = useState<'home' | 'saved'>('home');

  // Filters
  const [activeCategory, setActiveCategory] = useState('All');
  const [activeMake, setActiveMake] = useState('All');
  const [activeModel, setActiveModel] = useState('All Models');
  const [activeStores, setActiveStores] = useState<string[]>([]); 

  const VEHICLE_DATA: Record<string, string[]> = {
    'Tesla': ['Model 3', 'Model Y', 'Model S', 'Model X', 'Cybertruck'],
    'Rivian': ['R1T', 'R1S', 'Rivian'],
    'Lucid': ['Air', 'Gravity', 'Lucid']
  };

  const toggleStore = (storeName: string) => {
      if (storeName === 'All Stores') {
          setActiveStores(prev => prev.length === 0 ? ['NONE'] : []);
          return;
      }
      setActiveStores(prev => {
          if (prev.length === 0) {
              const allAvailableStores = Object.keys(STORE_MAP).filter(s => s !== 'All Stores');
              return allAvailableStores.filter(s => s !== storeName);
          }
          if (prev.length === 1 && prev[0] === 'NONE') {
              return [storeName];
          }
          const newStores = prev.includes(storeName) 
              ? prev.filter(s => s !== storeName) 
              : [...prev, storeName];
          return newStores.length === 0 ? ['NONE'] : newStores;
      });
  };

  const [activeBrand, setActiveBrand] = useState('All Brands');
  const [activeDealType, setActiveDealType] = useState('All Types');
  const [onlyHighDiscount, setOnlyHighDiscount] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [minPrice, setMinPrice] = useState<number | ''>('');
  const [maxPrice, setMaxPrice] = useState<number | ''>('');
  const [showFilters, setShowFilters] = useState(false);
  const [dealLimit, setDealLimit] = useState(1500);
  const [sortOption, setSortOption] = useState<'Newest' | 'Highest Discount'>('Newest');

  // Admin & User
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  
  // BULK EDIT STATE
  const [bulkType, setBulkType] = useState('No Change');
  const [bulkPromo, setBulkPromo] = useState('No Change');
  const [bulkStock, setBulkStock] = useState('No Change');

  // SHOW MODAL STATE 
  const [showQuickEditModal, setShowQuickEditModal] = useState(false);

  // Form State (Quick Edit)
  const [editingLiveDealId, setEditingLiveDealId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [price, setPrice] = useState('');
  const [originalPrice, setOriginalPrice] = useState('');
  const [brand, setBrand] = useState('Tesla');
  const [vehicle, setVehicle] = useState('Model Y');
  const [url, setUrl] = useState('');
  const [image, setImage] = useState('');
  const [category, setCategory] = useState('Accessories');
  const [dealType, setDealType] = useState('Sale');
  const [store, setStore] = useState('amz');
  const [promoType, setPromoType] = useState('None');
  const [inStock, setInStock] = useState(true);
  const [curatorNote, setCuratorNote] = useState('');

  // --- AUTH LISTENERS ---
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        if (currentUser) {
            setUser(currentUser);
            const userRef = doc(db, 'users', currentUser.uid);
            const userSnap = await getDoc(userRef);

            if (userSnap.exists()) {
                const data = userSnap.data();
                setUserData(data);
                if (data.role === 'admin') setIsAdmin(true);
            } else {
                const newProfile = {
                    uid: currentUser.uid,
                    email: currentUser.email,
                    displayName: currentUser.displayName || 'Deal Hunter',
                    photoURL: currentUser.photoURL,
                    role: 'user',
                    reputation: 0,
                    createdAt: Date.now()
                };
                await setDoc(userRef, newProfile);
                setUserData(newProfile);
            }
        } else {
            setUser(null);
            setUserData(null);
            setIsAdmin(false);
        }
    });
    const params = new URLSearchParams(window.location.search);
    if (params.get('admin') === 'true') setIsAdmin(true);
    fetchVideo();
    return () => unsubscribe();
  }, []);

  const fetchVideo = async () => {
    try {
      const docRef = doc(db, 'settings', 'featuredVideo');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) { setFeaturedVideo(docSnap.data() as any); }
    } catch (e) { console.log("No video config found"); }
  };

  useEffect(() => {
    const fetchHeroDeals = async () => {
        try {
            const qHero = query(collection(db, 'deals'), where('dealType', 'in', ['Glitch', 'Daily Deal']), orderBy('timestamp', 'desc'), limit(20));
            const snapHero = await getDocs(qHero);
            const activeHeroDocs = snapHero.docs.filter(d => d.data().status === 'active');
            
            setHeroDeals(activeHeroDocs.map(d => {
                const data = d.data() as Deal;
                let cleanImage = data.image;
                if (cleanImage && (cleanImage.includes('ytimg') || cleanImage.includes('youtube'))) {
                    cleanImage = "https://placehold.co/600x400/orange/white?text=Hot+Deal"; 
                }
                return { ...data, image: cleanImage, url: getHighCommissionLink(data.url), id: d.id };
            }));
        } catch (e) { console.log("Hero Fetch Error:", e); }
    };
    fetchHeroDeals();
  }, []);

  const handleOpenPostModal = () => {
   setEditingLiveDealId(null);
   setTitle('');
   setPrice('');
   setOriginalPrice('');
   setUrl('');
   setImage('');
   setCuratorNote('');
   setShowQuickEditModal(true);
};

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        let effectiveLimit = dealLimit;
        if (searchQuery.trim().length > 0 || activeStores.length > 10) effectiveLimit = 800;
        
        let constraints: any[] = [];
        constraints.push(where('status', '==', activeCategory === 'Community' ? 'pending' : 'active'));

        if (activeBrand !== 'All Brands') constraints.push(where('brand', '==', activeBrand));

        if (sortOption === 'Highest Discount') {
            constraints.push(orderBy('discountPercentage', 'desc')); 
        } else {
            constraints.push(orderBy('timestamp', 'desc'));
        }
        
        constraints.push(limit(effectiveLimit));

        const q = query(collection(db, 'deals'), ...constraints);
        const snapshot = await getDocs(q);
        
        const items = snapshot.docs.map(doc => {
            const data = doc.data() as Deal;
            let cleanImage = data.image;
            if (cleanImage && (cleanImage.includes('ytimg') || cleanImage.includes('youtube'))) cleanImage = "https://placehold.co/600x400/orange/white?text=See+Deal"; 
            return { ...data, image: cleanImage, url: getHighCommissionLink(data.url), id: doc.id };
        });

        setDeals(items); 
      } catch (error: any) { 
          console.error("Error fetching deals:", error);
          if (error.message.includes("requires an index")) {
              alert("⚠️ MISSING INDEX: Open your browser console (F12) and click the link from Firebase to fix this filter!");
          }
      }
      setLoading(false);
    };
    fetchData();
  }, [dealLimit, activeCategory, searchQuery, activeStores, activeBrand, sortOption]);

  const handleGoogleLogin = async () => {
    try {
        const provider = new GoogleAuthProvider();
        await signInWithPopup(auth, provider);
    } catch (error: any) { alert("Login Failed: " + error.message); }
  };

  const handleLogout = async () => { await signOut(auth); window.location.reload(); };

  const toggleSelection = (id: string) => {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) newSet.delete(id); else newSet.add(id);
    setSelectedIds(newSet);
  };

  const handleBlock = async (id: string) => {
    if (!window.confirm("🚫 Blacklist this item?")) return;
    try {
        await setDoc(doc(db, 'blacklist', id), { blockedAt: Date.now(), reason: "Manual Block" });
        await deleteDoc(doc(db, 'deals', id));
        setDeals(prev => prev.filter(d => d.id !== id));
        alert("Item Blocked.");
    } catch (e: any) { alert("Error: " + e.message); }
  };

  const handleBulkArchive = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`📦 Archive ${selectedIds.size} upgrades? They will be hidden from the live site but kept in your database.`)) return;
    
    try {
        const ids = Array.from(selectedIds);
        const batch = writeBatch(db);
        for (const id of ids) { 
            batch.update(doc(db, 'deals', id), {
                status: 'archived',
                archivedAt: Date.now()
            }); 
        }
        await batch.commit();
        setDeals(prev => prev.filter(d => !ids.includes(d.id!)));
        setHeroDeals(prev => prev.filter(d => !ids.includes(d.id!)));
        setSelectedIds(new Set()); 
        alert("✅ Upgrades archived successfully.");
    } catch (e: any) { alert("Error archiving: " + e.message); }
  };

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`🚨 PERMANENT DELETE: Are you absolutely sure? This will completely wipe ${selectedIds.size} upgrades from Firebase forever.`)) return;
    
    try {
        const ids = Array.from(selectedIds);
        const batch = writeBatch(db);
        for (const id of ids) { batch.delete(doc(db, 'deals', id)); }
        await batch.commit();
        setDeals(prev => prev.filter(d => !ids.includes(d.id!)));
        setHeroDeals(prev => prev.filter(d => !ids.includes(d.id!)));
        setSelectedIds(new Set()); 
        alert("✅ Upgrades permanently deleted.");
    } catch (e: any) { alert("Error deleting: " + e.message); }
  };

  const handleBulkAddNote = async () => {
    if (selectedIds.size === 0) return;
    const note = window.prompt("✍️ Enter Expert Insight for ALL selected upgrades:");
    if (!note) return; 
    
    try {
        setLoading(true);
        const ids = Array.from(selectedIds);
        const batch = writeBatch(db);
        for (const id of ids) {
            batch.update(doc(db, 'deals', id), { 
                curatorNote: note,
                manualLock: true
            });
        }
        await batch.commit();
        setDeals(prev => prev.map(d => ids.includes(d.id!) ? { ...d, curatorNote: note, manualLock: true } : d));
        alert(`✅ Insight added to ${ids.length} upgrades!`);
        setSelectedIds(new Set());
        setLoading(false);
    } catch (e: any) { alert("Error: " + e.message); setLoading(false); }
  };

  const handleBulkUpdate = async () => {
    if (selectedIds.size === 0) return;
    if (bulkType === 'No Change' && bulkPromo === 'No Change' && bulkStock === 'No Change') return alert("Select a change to apply.");
    if (!window.confirm(`Apply these updates to ${selectedIds.size} upgrades?`)) return;

    try {
        setLoading(true);
        const ids = Array.from(selectedIds);
        const batch = writeBatch(db);
        
        for (const id of ids) {
            const updates: any = {};
            if (bulkType !== 'No Change') {
                updates.dealType = bulkType;
                if (bulkType === 'Sale') updates.category = 'Accessories'; 
            }
            if (bulkPromo !== 'No Change') {
                updates.promoType = bulkPromo;
            }
            if (bulkStock !== 'No Change') {
                const isNowInStock = bulkStock === 'In Stock';
                updates.inStock = isNowInStock;
                updates.status = isNowInStock ? 'active' : 'hidden';
            }
            batch.update(doc(db, 'deals', id), updates);
        }
       await batch.commit();
        if (bulkStock === 'Out of Stock') {
            setDeals(prev => prev.filter(d => !ids.includes(d.id!)));
            setHeroDeals(prev => prev.filter(d => !ids.includes(d.id!)));
            setSelectedIds(new Set());
        } else {
            window.location.reload(); 
        }
    } catch (e: any) { alert("Error: " + e.message); setLoading(false); }
  };

  const handleEditLiveDeal = (deal: Deal) => {
      setEditingLiveDealId(deal.id!); 
      setTitle(deal.title); 
      setPrice(String(deal.price)); 
      setOriginalPrice(String(deal.originalPrice));
      setBrand(deal.brand || 'Tesla');
      setVehicle(deal.vehicle || 'Model Y');
      setUrl(deal.url); 
      setImage(deal.image || ''); 
      setCategory(deal.category || 'Accessories');
      setDealType(deal.dealType || 'Sale'); 
      setPromoType(deal.promoType || 'None'); 
      setInStock(deal.inStock !== false); 
      setCuratorNote(deal.curatorNote || '');
      setStore(Object.keys(STORE_MAP).find(key => STORE_MAP[key] === deal.store) ? STORE_MAP[Object.keys(STORE_MAP).find(key => STORE_MAP[key] === deal.store)!] : 'amz');
      setShowQuickEditModal(true);
  };

  const handlePostDeal = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!title || !price || !url) return alert("Missing Fields!");

    const dbStore = STORE_MAP[store] || store;

    const dealData: any = { 
        title, 
        price: parseFloat(price), 
        originalPrice: parseFloat(originalPrice) || parseFloat(price), 
        url, 
        image: image || "https://placehold.co/600x400/blue/white?text=HOT+UPGRADE&font=roboto", 
        category, 
        dealType, 
        brand,
        vehicle,
        promoType,
        inStock,
        store: dbStore,
        curatorNote, 
        hot: true,
        manualLock: true, 
        status: inStock ? 'active' : 'hidden' 
    };

    if (!editingLiveDealId) dealData.timestamp = serverTimestamp();

    try {
     if (editingLiveDealId) {
          await updateDoc(doc(db, 'deals', editingLiveDealId), dealData);
          const updatedDeal = { ...dealData, id: editingLiveDealId };
          
          if (dealData.status === 'hidden') {
              setDeals(prev => prev.filter(d => d.id !== editingLiveDealId));
              setHeroDeals(prev => prev.filter(d => d.id !== editingLiveDealId));
          } else {
              setDeals(prev => prev.map(d => d.id === editingLiveDealId ? updatedDeal : d));
              if (dealType === 'Glitch' || dealType === 'Daily Deal') { 
                  setHeroDeals(prev => { 
                      const exists = prev.find(g => g.id === editingLiveDealId); 
                      if (exists) return prev.map(g => g.id === editingLiveDealId ? updatedDeal : g); 
                      return [updatedDeal, ...prev]; 
                  }); 
              }
          }
          alert("✅ Updated!");
          setEditingLiveDealId(null);
          setShowQuickEditModal(false);
      } else {
          const docRef = await addDoc(collection(db, 'deals'), dealData);
          const newDeal = { ...dealData, id: docRef.id };
          setDeals(prev => [newDeal, ...prev]);
          if (dealType === 'Glitch' || dealType === 'Daily Deal') setHeroDeals(prev => [newDeal, ...prev]);
          alert("✅ Posted!");
      }
      setTitle(''); setPrice(''); setPromoType('None'); setInStock(true); setOriginalPrice(''); setUrl(''); setImage(''); setCuratorNote('');
    } catch (error: any) { alert("Error: " + error.message); }
  };

  const handleDelete = async (id: string) => {
    if(!window.confirm("Archive this upgrade? (It will vanish from the site but your curation notes will be safely saved in the database)")) return;
    try {
        await updateDoc(doc(db, 'deals', id), { status: 'archived', archivedAt: Date.now() });
        setDeals(prev => prev.filter(d => d.id !== id));
        setHeroDeals(prev => prev.filter(d => d.id !== id));
    } catch (error: any) { alert("Error archiving: " + error.message); }
  };

  const handleSubscribe = async (e: React.FormEvent) => { 
      e.preventDefault(); 
      if (!email.includes('@')) return alert("Invalid email"); 
      try { 
          await setDoc(doc(db, 'subscribers', email), { email, joinedAt: Date.now() }); 
          setSubscribed(true); 
          setEmail(''); 
          setTimeout(() => setSubscribed(false), 5000); 
      } catch (err) {} 
  };

  const filteredDeals = deals.filter(deal => {
    const searchLower = searchQuery.toLowerCase();
    const matchesSearch = 
        (deal.title?.toLowerCase() || '').includes(searchLower) || 
        (deal.store?.toLowerCase() || '').includes(searchLower) ||
        ((deal as any).brand?.toLowerCase() || '').includes(searchLower) ||
        (deal.category?.toLowerCase() || '').includes(searchLower) ||
        (deal.description?.toLowerCase() || '').includes(searchLower);
        
    if (activeMake !== 'All') {
        const dealVehicles = (deal.vehicle || '').toLowerCase();
        const makeLower = activeMake.toLowerCase();
        
        // Let "All Vehicles" bypass the strict filter
        const isAllVehicles = dealVehicles === 'all vehicles' || dealVehicles === 'universal';

        // Check if ANY of the make's models are included in the deal's vehicle string
        const isCorrectMake = isAllVehicles || dealVehicles.includes(makeLower) || VEHICLE_DATA[activeMake].some(m => dealVehicles.includes(m.toLowerCase()));
        
        if (!isCorrectMake) return false;

        // Check if the specific model is included
        if (activeModel !== 'All Models') {
            const modelLower = activeModel.toLowerCase();
            if (!isAllVehicles && !dealVehicles.includes(modelLower)) return false;
        }
    }
    if (!matchesSearch) return false;
    if (activeCategory === 'Community') return deal.status === 'pending';

    if (activeStores.length > 0) {
        if (activeStores.includes('NONE')) return false; 
        let isMatch = false;
        for (const targetStore of activeStores) {
            const dbStore = (deal.store || '').toLowerCase().trim();
            const target = targetStore.toLowerCase().trim();
            if (dbStore === target || dbStore.includes(target) || target.includes(dbStore)) {
                isMatch = true; break;
            }
            const storeId = STORE_MAP[targetStore];
            if (storeId) {
                if (dbStore.includes(storeId)) { isMatch = true; break; }
                if (STORE_VARIANTS[storeId] && STORE_VARIANTS[storeId].some((v: string) => v.toLowerCase() === dbStore)) { isMatch = true; break; }
            }
        }
        if (!isMatch) return false;
    }

    if (activeCategory !== 'All' && deal.category !== activeCategory) return false; 
    
    if (activeDealType !== 'All Types') {
       const dbType = (deal.dealType || '').toLowerCase();
       const title = (deal.title || '').toLowerCase();
       if (activeDealType === 'Buy More Save More') {
           if (!(dbType === 'bundle' || dbType === 'buy more save more' || title.includes('buy more') || title.includes('save more'))) return false;
       } 
       else if (activeDealType === 'BOGO & Free Gift') {
           if (!(dbType.includes('free') || dbType.includes('bogo') || dbType.includes('gift') || title.includes('free') || title.includes('bogo'))) return false;
       }
       else if (activeDealType === 'Sale') {
           if (dbType !== 'sale' && dbType !== 'clearance') return false; 
       }
       else { if (deal.dealType !== activeDealType) return false; }
    }
    if (minPrice !== '' && deal.price < Number(minPrice)) return false;
    if (maxPrice !== '' && deal.price > Number(maxPrice)) return false;
    if (onlyHighDiscount) {
        let discount = 0;
        if (deal.originalPrice > deal.price) discount = ((deal.originalPrice - deal.price) / deal.originalPrice) * 100;
        if (discount < 50) return false;
    }
    return true;
  });

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-slate-800 flex flex-col relative">

      {/* QUICK EDIT MODAL */}
      {showQuickEditModal && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-sm p-4">
            <div className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden animate-slideUp">
                <div className="bg-slate-800 p-4 border-b border-slate-700 flex justify-between items-center">
                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                        <Zap className="text-blue-500 w-5 h-5" /> Quick Edit
                    </h3>
                    <button onClick={() => { setShowQuickEditModal(false); setEditingLiveDealId(null); }} className="text-slate-400 hover:text-white transition-colors">
                        <X className="w-6 h-6" />
                    </button>
                </div>
                <form onSubmit={(e) => handlePostDeal(e)} className="p-6 space-y-4 text-left">
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Title</label>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} required className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500" placeholder="e.g. Matte Screen Protector" />
        </div>
        <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Affiliate Link</label>
            <input type="url" value={url} onChange={(e) => setUrl(e.target.value)} required className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500" />
        </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
        <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Vehicle Fitment</label>
            <select value={vehicle} onChange={(e) => setVehicle(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500">
                {['Model Y', 'Model 3', 'Model S', 'Model X', 'Cybertruck', 'Rivian', 'Lucid', 'All Vehicles'].map(v => (
                    <option key={v} value={v}>{v}</option>
                ))}
            </select>
        </div>
        <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Brand</label>
            <select value={brand} onChange={(e) => setBrand(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500">
                {['Tesla', 'Spigen', 'Tesmanian', 'Abstract Ocean', 'Michelin', 'Hanshow', 'EVBase', 'Tesstudio', 'Tesery'].sort().map(b => (
                    <option key={b} value={b}>{b}</option>
                ))}
            </select>
        </div>
    </div>

    <div className="grid grid-cols-2 gap-4">
        <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Price</label>
            <input type="number" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)} required className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500" />
        </div>
        <div>
            <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Store</label>
            <select value={store} onChange={(e) => setStore(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500">
                {Object.keys(STORE_MAP).filter(s => s !== 'All Stores').map(s => (
                    <option key={s} value={s}>{s}</option>
                ))}
            </select>
        </div>
    </div>

    <div>
        <label className="block text-xs font-bold text-slate-400 uppercase mb-1">Image URL</label>
        <input type="text" value={image} onChange={(e) => setImage(e.target.value)} className="w-full bg-slate-800 border border-slate-600 rounded-lg p-3 text-white outline-none focus:border-blue-500" />
    </div>

    <button type="submit" className="w-full h-[50px] bg-blue-600 hover:bg-blue-500 text-white font-black text-lg rounded-xl shadow-lg mt-2 transition-transform active:scale-95">
        {editingLiveDealId ? 'Update Upgrade' : 'Post to Garage'}
    </button>
</form>
            </div>
        </div>
      )}

      {/* BULK ACTION BAR */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-slate-900 text-white p-4 z-[100] shadow-2xl flex flex-col xl:flex-row items-center justify-between animate-slideUp border-t-4 border-red-500 gap-4 overflow-x-auto">
            <div className="flex items-center gap-4 px-4 whitespace-nowrap">
                <span className="font-bold text-lg">{selectedIds.size} Selected</span>
                <button onClick={() => setSelectedIds(new Set())} className="text-slate-400 text-sm hover:text-white underline">Cancel</button>
            </div>
            
            <div className="flex flex-wrap sm:flex-nowrap items-center justify-center gap-2">
                <select value={bulkType} onChange={(e) => setBulkType(e.target.value)} className="bg-slate-800 text-white border border-slate-600 rounded px-2 py-2 text-xs focus:border-blue-500 outline-none">
                    <option value="No Change">Upgrade Type...</option>
                    <option value="Sale">Regular Deal (Sale)</option>
                    <option value="Glitch">🔥 Glitch</option>
                    <option value="Clearance">Clearance</option>
                    <option value="Daily Deal">Daily Deal</option>
                </select>

                <select value={bulkPromo} onChange={(e) => setBulkPromo(e.target.value)} className="bg-slate-800 text-white border border-slate-600 rounded px-2 py-2 text-xs focus:border-blue-500 outline-none">
                    <option value="No Change">Promo Type...</option>
                    <option value="None">None</option>
                    <option value="BOGO">BOGO</option>
                    <option value="BMSM">BMSM</option>
                    <option value="Free Gift">Free Gift</option>
                    <option value="Bundle">Bundle</option>
                </select>

                <select value={bulkStock} onChange={(e) => setBulkStock(e.target.value)} className="bg-slate-800 text-white border border-slate-600 rounded px-2 py-2 text-xs focus:border-blue-500 outline-none">
                    <option value="No Change">Stock Status...</option>
                    <option value="In Stock">✅ In Stock</option>
                    <option value="Out of Stock">❌ Out of Stock</option>
                </select>

                <button onClick={handleBulkUpdate} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-3 rounded shadow-lg flex items-center gap-2 text-sm">
                    <FolderInput className="w-4 h-4" /> Apply
                </button>
            </div>

            <div className="flex gap-2 px-4 xl:border-l xl:border-slate-700 xl:ml-4 xl:pl-4 flex-wrap justify-center">
                <button onClick={handleBulkAddNote} className="bg-blue-600 hover:bg-blue-500 text-white font-bold py-2 px-4 rounded shadow-lg flex items-center gap-2 whitespace-nowrap">
                    <Edit className="w-5 h-5" /> Mass Curate
                </button>
                <button onClick={handleBulkArchive} className="bg-orange-600 hover:bg-orange-500 text-white font-bold py-2 px-4 rounded shadow-lg flex items-center gap-2 border border-orange-500">
                    <Archive className="w-5 h-5" /> Archive
                </button>
                <button onClick={handleBulkDelete} className="bg-red-700 hover:bg-red-600 text-white font-bold py-2 px-4 rounded shadow-lg flex items-center gap-2">
                    <Trash2 className="w-5 h-5" /> Delete
                </button>
            </div>
        </div>
      )}

      {/* NAVBAR */}
      <Navbar 
  searchQuery={searchQuery} setSearchQuery={setSearchQuery} 
  minPrice={minPrice} setMinPrice={setMinPrice} 
  maxPrice={maxPrice} setMaxPrice={setMaxPrice} 
  view={view} setView={setView} 
  user={user} userData={userData} isAdmin={isAdmin} 
  handleGoogleLogin={handleGoogleLogin} handleLogout={handleLogout} 
  onPostClick={handleOpenPostModal} setShowFilters={setShowFilters} 
  activeBrand={activeBrand} onlyHighDiscount={onlyHighDiscount} 
/>

     <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col gap-8 flex-1 w-full overflow-x-hidden">
  <SectionToggle />
  <Routes>
    <Route path="/" element={
      <>
        {view === 'saved' ? (
            <div className="w-full">
                <SavedDeals />
            </div>
        ) : (
            <>
                <div className="flex-1 w-full">
                {featuredVideo?.videoId && (
                    <div className="max-w-3xl mx-auto mb-8 bg-slate-900 rounded-xl overflow-hidden shadow-xl border border-slate-700">
                        <div className="p-4 bg-slate-800 border-b border-slate-700 flex items-center justify-between">
                            <h2 className="text-white font-bold text-lg flex items-center gap-2">
                                <span className="text-red-500">▶</span> Latest EV Upgrades
                            </h2>
                            {featuredVideo?.title && (
                                <span className="text-slate-400 text-sm hidden sm:block truncate ml-4">
                                    {featuredVideo.title}
                                </span>
                            )}
                        </div>
                        <div className="relative w-full aspect-video bg-black">
                            <iframe className="absolute top-0 left-0 w-full h-full" src={`https://www.youtube.com/embed/${featuredVideo.videoId}?rel=0`} title={featuredVideo?.title || "Latest EV Upgrades Video"} frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen></iframe>
                        </div>
                    </div>
                )}

                {heroDeals.length > 0 && activeCategory !== 'Community' && (
                    <div className="mb-8">
                    <div className="flex items-center gap-2 mb-4 bg-slate-900 text-white p-3 rounded-lg shadow-md border-l-4 border-blue-500">
                        <Zap className="w-6 h-6 fill-blue-400 text-blue-400 animate-pulse" />
                        <h2 className="text-lg font-extrabold uppercase tracking-wider">Urgent: Flash Upgrades & Glitches</h2>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        {heroDeals.map((deal, index) => (
                        <DealCard priority={index < 4} key={deal.id} deal={deal} isAdmin={isAdmin} isSelected={selectedIds.has(deal.id!)} onToggleSelect={toggleSelection} onEdit={handleEditLiveDeal} onDelete={handleDelete} onBlock={handleBlock} />
                        ))}
                    </div>
                    </div>
                )}

                {/* 🆕 TIERED MAKE, MODEL, & STORE FILTER */}
                <div className="mb-10 flex flex-col md:flex-row gap-4">
                  <div className="w-full md:w-48">
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Make</label>
                    <select 
                      value={activeMake} 
                      onChange={(e) => {
                        setActiveMake(e.target.value);
                        setActiveModel('All Models');
                      }}
                      className="bg-white border border-slate-200 text-slate-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500 block w-full p-3 font-bold shadow-sm cursor-pointer appearance-none transition-all"
                    >
                      <option value="All">All Makes</option>
                      {Object.keys(VEHICLE_DATA).map(make => (
                        <option key={make} value={make}>{make}</option>
                      ))}
                    </select>
                  </div>

                  {activeMake !== 'All' && (
                    <div className="w-full md:w-64 animate-fadeIn">
                      <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Model</label>
                      <select 
                        value={activeModel} 
                        onChange={(e) => setActiveModel(e.target.value)}
                        className="bg-white border border-slate-200 text-slate-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500 block w-full p-3 font-bold shadow-sm cursor-pointer appearance-none transition-all"
                      >
                        <option value="All Models">All {activeMake} Models</option>
                        {VEHICLE_DATA[activeMake].map(model => (
                          <option key={model} value={model}>{model}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* 🏬 STORE FILTER DROPDOWN */}
                  <div className="w-full md:w-48">
                    <label className="block text-xs font-bold text-slate-500 mb-2 uppercase tracking-wider">Store</label>
                    <select 
                      value={activeStores.length === 1 ? activeStores[0] : 'All Stores'} 
                      onChange={(e) => {
                          if (e.target.value === 'All Stores') setActiveStores([]);
                          else setActiveStores([e.target.value]);
                      }}
                      className="bg-white border border-slate-200 text-slate-900 text-sm rounded-xl focus:ring-2 focus:ring-blue-500 block w-full p-3 font-bold shadow-sm cursor-pointer appearance-none transition-all"
                    >
                      <option value="All Stores">All Stores</option>
                      {Object.keys(STORE_MAP).filter(s => s !== 'All Stores').sort().map(store => (
                        <option key={store} value={store}>{store}</option>
                      ))}
                    </select>
                  </div>
                </div>
                
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
                        {[...Array(9)].map((_, i) => (
                            <DealSkeleton key={`skeleton-${i}`} />
                        ))}
                    </div>
                ) : filteredDeals.length === 0 ? (
                    <div className="text-center py-20 bg-white rounded-2xl border border-dashed border-gray-300">
                        <AlertCircle className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                        <p className="text-gray-500">No upgrades found matching your filters.</p>
                        <button onClick={() => {setActiveCategory('All'); setActiveBrand('All Brands'); setActiveStores([]); setMinPrice(''); setMaxPrice(''); setSearchQuery('')}} className="mt-4 text-blue-600 font-bold text-sm hover:underline">Clear all filters</button>
                    </div>
                ) : (
                    <>
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-4 gap-4">
                        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                            {activeCategory === 'Community' ? 'Pending Community Upgrades' : (activeStores.length === 0 ? 'All' : (activeStores.includes('NONE') ? '0' : (activeStores.length === 1 ? activeStores[0] : 'Multiple Stores'))) + ' Upgrades'}
                            <span className="ml-2 text-sm font-normal text-gray-500 bg-gray-100 px-2 py-1 rounded-full border border-gray-200">
                                {filteredDeals.length} {activeCategory === 'Community' ? 'pending' : 'active'}
                            </span>
                        </h2>
                        
                        <div className="flex items-center gap-4 flex-wrap">
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Brand:</span>
                                <select value={activeBrand} onChange={(e) => setActiveBrand(e.target.value)} className="bg-white border border-gray-300 text-slate-800 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm cursor-pointer font-bold transition-all hover:border-gray-400">
                                    <option value="All Brands">All Brands</option>
                                    {['Tesla', 'Spigen', 'Tesmanian', 'Abstract Ocean', 'Michelin', 'Hanshow', 'EVBase', 'Tesstudio', 'Tesery'].sort((a, b) => a.localeCompare(b)).map(brand => (
                                        <option key={brand} value={brand}>{brand}</option>
                                    ))}
                                </select>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className="text-sm font-bold text-gray-500 uppercase tracking-wider">Sort By:</span>
                                <select value={sortOption} onChange={(e) => setSortOption(e.target.value as any)} className="bg-white border border-gray-300 text-slate-800 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 outline-none shadow-sm cursor-pointer font-bold transition-all hover:border-gray-400">
                                    <option value="Newest">🔥 Newest Upgrades</option>
                                    <option value="Highest Discount">📉 Highest Discount</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
                        {filteredDeals.map((deal) => (
                        <DealCard key={deal.id} deal={deal} isAdmin={isAdmin} isSelected={selectedIds.has(deal.id!)} onToggleSelect={toggleSelection} onEdit={handleEditLiveDeal} onDelete={handleDelete} onBlock={handleBlock} />
                        ))}
                    </div>
                    {!searchQuery && (<div className="flex justify-center pb-12"><button onClick={() => setDealLimit(prev => prev + 50)} className="bg-white border border-gray-300 hover:bg-gray-50 text-gray-700 font-bold py-3 px-8 rounded-full shadow-sm transition-all flex items-center gap-2">Load More Upgrades</button></div>)}
                    <div className="bg-slate-900 rounded-xl p-6 mb-8 text-center md:text-left flex flex-col md:flex-row items-center gap-6 shadow-xl border border-slate-700">
                      <div className="flex-1"><h3 className="text-white font-bold text-xl flex items-center justify-center md:justify-start gap-2"><Mail className="text-blue-500 w-6 h-6" /> Get Upgrade Alerts Instantly</h3>
<p className="text-slate-400 text-sm mt-1">Don't miss the next price drop. We'll email you the second a top-tier EV accessory goes on sale.</p></div>
                      <form onSubmit={handleSubscribe} className="w-full md:w-auto flex flex-col sm:flex-row gap-2">{subscribed ? <div className="bg-green-600 text-white px-6 py-2.5 rounded-lg font-bold flex items-center justify-center gap-2 w-full md:w-80">✅ You're on the list!</div> : (<><input type="email" placeholder="Enter your email..." className="px-4 py-2.5 rounded-lg bg-slate-800 border border-slate-600 text-white placeholder-slate-400 focus:outline-none focus:border-blue-500 w-full md:w-64" value={email} onChange={(e) => setEmail(e.target.value)} /><button type="submit" className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-2.5 rounded-lg flex items-center justify-center gap-2 transition-colors">Subscribe <Send className="w-4 h-4" /></button></>)}</form>
                  </div>
                    </>
                )}
                </div> 
            </>
        )} 
      </>
    } />
    <Route path="/news" element={<EvNews />} />
    <Route path="/costs" element={<CostsAndSavings />} />
    <Route path="/deal/:id" element={<DealPage />} />
    <Route path="/terms" element={<Terms />} />
  </Routes>
</main>
<Footer />
    </div>
  );
}